import { useState, useEffect } from "react";
import { Users, Mail, Disc3, Radio, Building2, Clock, ClipboardList, TrendingUp, UserPlus, BadgeCheck } from "lucide-react";
import { api } from "@/lib/api";
import { StatCard } from "@/components/dashboard/ui";
import StreamChart from "@/components/charts/StreamChart";

export default function AdminOverview({ stats, goto }) {
  const s = stats || {};
  const [analytics, setAnalytics] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/analytics");
        setAnalytics(data);
      } catch (e) {
        /* ignore */
      }
    })();
  }, []);
  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div>
        <h2 className="font-display text-2xl md:text-3xl tracking-tight text-white">Platform at a glance</h2>
        <p className="mt-2 text-slate-400">Manage your roster, invitations and releases from one place.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label="Artists" value={s.total_artists ?? "—"} sub="Active artist accounts" icon={Users} />
        <StatCard label="Labels" value={s.total_labels ?? "—"} sub="Label workspaces" icon={Building2} />
        <StatCard label="Pending invites" value={s.pending_invites ?? "—"} sub="Awaiting sign-in" icon={Mail} />
        <StatCard label="Active members" value={s.active_users ?? "—"} sub="Enabled accounts" icon={Radio} />
        <StatCard label="Pending approval" value={s.pending_members ?? "—"} sub="Awaiting your review" icon={UserPlus} />
        <StatCard label="Subscribed" value={s.subscribed ?? "—"} sub="Distribution active" icon={BadgeCheck} />
        <StatCard label="Total releases" value={s.total_releases ?? "—"} sub="Across all artists" icon={Disc3} />
        <StatCard label="Live" value={s.live_releases ?? "—"} sub="Delivered to stores" icon={TrendingUp} />
        <StatCard label="In review" value={s.in_review ?? "—"} sub="Needs your approval" icon={Clock} />
        <StatCard label="Waitlist" value={s.waitlist_count ?? "—"} sub="Access requests" icon={ClipboardList} />
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-display text-xl text-white">Platform streams</h3>
            <p className="text-sm text-slate-500 mt-1">
              {(analytics?.total_streams ?? 0).toLocaleString()} streams across {analytics?.live_count ?? 0} live releases · last 30 days
            </p>
          </div>
          <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-amber-300 border border-amber-500/30 rounded-full px-3 py-1">
            Demo data
          </span>
        </div>
        <StreamChart data={analytics?.series || []} height={240} />
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h3 className="font-display text-xl text-white">Grow your roster</h3>
          <p className="mt-1.5 text-sm text-slate-400 max-w-md">Invite a new artist or label. They'll sign in with Google to activate their workspace instantly.</p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="overview-invite-btn"
            onClick={() => goto("invitations")}
            className="inline-flex items-center rounded-full bg-white text-black font-medium px-6 py-3 hover:bg-slate-200 transition-colors"
          >
            Send an invite
          </button>
          <button
            data-testid="overview-review-btn"
            onClick={() => goto("releases")}
            className="inline-flex items-center rounded-full border border-white/15 px-6 py-3 hover:bg-white/5 transition-colors"
          >
            Review releases
          </button>
        </div>
      </div>
    </div>
  );
}
